import {
  REPORT_MAX_WINDOW_DAYS,
  scriptStepsReportParamSchema,
} from '@aica/api-types/schemas';
import {
  parseObjectionsSearch,
  streamSearchParams,
  type ObjectionsPeriod,
} from '@/components/analytics/objections/objections-search';
import type { useAppNavigate } from '@/hooks/useAppNavigate';

/**
 * Параметры адреса экрана «Соблюдение скрипта» (#879) — всё его состояние:
 * период с опорной датой, проекты и раскрытый шаг. В адресе, а не в
 * состоянии компонента: ссылка на шаг должна открывать его же, а «Назад» —
 * возвращать туда, где человек был.
 *
 * Период, опорная дата и проекты читаются так же, как у «Возражений»: один
 * разбор на оба экрана, поэтому переход с дашборда и между экранами раздела не
 * расходится в том, что считать верным значением.
 */
export interface ScriptStepsSearch {
  period?: ObjectionsPeriod;
  /** Any instant inside the shown period (ISO) — the dashboard's, when opened from it. */
  anchor?: string;
  projectIds?: string[];
  /** An exact window, its day and the demo rule of the dashboard card (`ObjectionsSearch`). */
  from?: string;
  to?: string;
  day?: string;
  demo?: 'yes' | 'no';
  /** The expanded step (`script_steps.id`, a UUID). */
  step?: string;
}

/**
 * Идентификатор шага читается правилом сервера, а не своей копией: любой id,
 * который вернул отчёт, должен пережить разбор адреса. Клиент кладёт его в путь
 * через `encodeURIComponent`, поэтому строже сервера быть незачем.
 */
const stepIdSchema = scriptStepsReportParamSchema.shape.stepId;

export function parseScriptStepsSearch(
  search: Record<string, unknown>
): ScriptStepsSearch {
  const { period, anchor, projectIds, from, to, day, demo } =
    // The report routes' limit, not the stream's: the two are separate numbers.
    parseObjectionsSearch(search, REPORT_MAX_WINDOW_DAYS);
  const parsedStep = stepIdSchema.safeParse(search.step);
  const step = parsedStep.success ? parsedStep.data : undefined;
  return {
    ...(period ? { period } : {}),
    ...(anchor ? { anchor } : {}),
    ...(projectIds ? { projectIds } : {}),
    ...(from && to ? { from, to } : {}),
    ...(day ? { day } : {}),
    ...(demo ? { demo } : {}),
    ...(step ? { step } : {}),
  };
}

/**
 * Открыть экран с фильтром виджета «Пропущенные шаги» — тем же периодом (с
 * опорной датой: на дашборде могли уйти стрелкой в прошлый месяц) и теми же
 * проектами. Во встроенном Bitrix24 у экрана нет вкладки: `useAppNavigate`
 * откроет портал отдельным окном.
 */
export function openScriptStepsScreen(
  appNavigate: ReturnType<typeof useAppNavigate>['appNavigate'],
  filter: { period?: string; projectIds?: readonly string[]; anchor?: string }
): void {
  const query = new URLSearchParams(streamSearchParams(filter)).toString();
  appNavigate(`/analytics/script-steps${query ? `?${query}` : ''}`);
}
